import React, { useEffect, useRef, useState } from 'react';

/**
 * Scroll-reveal wrapper — fades + slides children into view when the
 * section enters the viewport. Wrap any landing-page section in
 * <ScrollReveal> and you get the modern "appears as you scroll" effect
 * without pulling in framer-motion.
 *
 * Uses IntersectionObserver (native, ~50 LOC, no deps). Reveals ONCE
 * then disconnects — re-scrolling up doesn't replay the animation
 * (matches Linear/Vercel behavior).
 *
 * Props:
 *   - delay   (ms): stagger between sibling reveals. Default 0.
 *   - offset  (px): how many pixels INTO the viewport before the
 *                   reveal fires. Default 100 — fires when ~100px of
 *                   the section is visible.
 *   - className: extra Tailwind classes for the wrapper (rare).
 *
 * Respects `prefers-reduced-motion` — if the user has motion disabled
 * in their OS, content appears instantly with no animation.
 */
export default function ScrollReveal({
  children,
  delay = 0,
  offset = 100,
  className = '',
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Read OS-level "reduced motion" preference once on mount.
  // If true, we skip the IntersectionObserver and render immediately.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const onChange = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setVisible(true);
      return;
    }
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      // SSR or very old browser → fall back to instant-visible.
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Stagger reveals using a small setTimeout so sibling sections
            // can fade in sequentially without all firing at once.
            const t = setTimeout(() => setVisible(true), delay);
            observer.disconnect(); // reveal once, then stop watching
            return () => clearTimeout(t);
          }
        });
      },
      {
        // Use a negative bottom rootMargin so the reveal fires only AFTER
        // the section is meaningfully on-screen, not the moment its top
        // edge crosses the viewport.
        rootMargin: `0px 0px -${offset}px 0px`,
        threshold: 0,
      }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [delay, offset, prefersReducedMotion]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8'
      } ${className}`}
      // Defensive: make sure the section still takes layout space even
      // while hidden — opacity-0 alone preserves layout, but translate-y
      // is safer than display:none.
    >
      {children}
    </div>
  );
}
